import {
  getDubCallMesh,
  getDellCallMesh,
  getCiPosEndBarMesh,
  getCiPosEndVertMesh,
  drawCall,
  updateCallConf,
  updateCall,
  drawVert,
} from "./dellyBlueprint";
import DellyCnv from "./models/DellyCnv";
import getReadTicks from "./getReadTicks";
import { PATHOGENICITY_CL_DELLY } from "../../constants/options";

import ColorScale from "../../models/ColorScale";
import partition from "../../helpers/partition";
import { getSelectionColorMap } from "../../constants/colors";
import getFilters from "./getFilters";

function getDefaultState(track) {
  const { sampleId, analysisId } = track.getSharedState();
  return {
    filters: getFilters(),
    sampleId,
    colors: [],
    dellyCallMap: {},
    dellyDups: [],
    analysisId,
    dellyDels: [],
    ciPosEndBars: [],
    ciPosEndVerts: [],
    filterCounter: new Map(),
    max: -Infinity,
  };
}

function readTrackController(track) {
  const dubCallMesh = getDubCallMesh();
  const dellCallMesh = getDellCallMesh();
  const ciPosEndBarMesh = getCiPosEndBarMesh();
  const ciPosEndVertStartsMesh = getCiPosEndVertMesh();
  const ciPosEndVertEndsMesh = getCiPosEndVertMesh();

  let state = getDefaultState(track);

  function onReset() {
    state = getDefaultState(track);
  }

  function onRangeChange({ chrSegments }) {
    const seqs = chrSegments.map((x) => ({
      ...x,
      x0: x.chr.region.x0,
      x1: x.chr.region.x1,
      g0: x.chr.region.g0,
      g1: x.chr.region.g1,
    }));
    track.loadPosition(seqs, state);
  }

  function onDataReceived({ res, chr }) {
    const dellyCalls = (res || [])
      .map((x) => new DellyCnv(x, chr))
      .filter((x) => !state.dellyCallMap[x.id]);
    const max = Math.max(state.max, ...dellyCalls.map((x) => x.readratio));
    const maxCeil = Math.ceil(max);
    const maxRound = Math.round(max);
    const maxFloor = Math.floor(max);
    const [dups, dels] = partition(dellyCalls, (x) => x.isDeletion());
    const ciPosEnds = [
      ...dellyCalls.map((x) => x.ciPosRegion),
      ...dellyCalls.map((x) => x.ciEndRegion),
    ];

    for (let i = 0; i < dellyCalls.length; i++) {
      const call = dellyCalls[i];
      state.dellyCallMap[call.id] = call;
      if (call.isDeletion()) state.dellyDels.push(call);
      if (!call.isDeletion()) state.dellyDups.push(call);
    }

    for (let i = 0; i < ciPosEnds.length; i++) {
      state.ciPosEndBars.push(ciPosEnds[i]);
    }

    state.max = maxCeil === maxRound ? maxCeil : maxFloor + 0.5;
    dubCallMesh.uniforms.setMinY(state.max).setMaxY(0);
    dellCallMesh.uniforms.setMinY(state.max).setMaxY(0);
    ciPosEndBarMesh.uniforms.setMinY(state.max).setMaxY(0);
    ciPosEndVertEndsMesh.uniforms.setMinY(state.max).setMaxY(0);
    ciPosEndVertStartsMesh.uniforms.setMinY(state.max).setMaxY(0);

    dubCallMesh.setItems(state.dellyDups);
    dellCallMesh.setItems(state.dellyDels);
    ciPosEndBarMesh.setItems(state.ciPosEndBars);
    ciPosEndVertStartsMesh.setItems(state.ciPosEndBars);
    ciPosEndVertEndsMesh.setItems(state.ciPosEndBars);

    drawCall(dups, dubCallMesh);
    drawCall(dels, dellCallMesh);
    drawCall(ciPosEnds, ciPosEndBarMesh);
    drawVert(ciPosEnds, ciPosEndVertStartsMesh, true);
    drawVert(ciPosEnds, ciPosEndVertEndsMesh, false);

    updateFilterCounter(dellyCalls);
    calcFilters();
    applyFilters();

    track.setAxisProps({ ticks: getReadTicks(state.max) });
    track.requestRender();
  }

  function onPreferencesChanged() {
    const config = track.getPreferences();
    state.config = config;
    const { isDark } = config;
    const scaleName = config[PATHOGENICITY_CL_DELLY];
    const vec = getSelectionColorMap(isDark).setW(0.7);

    const theme = isDark ? 2 : 1;
    const colorScale = new ColorScale(scaleName, 5).setTheme(theme);

    state.colors = colorScale.hexes;

    dubCallMesh.uniforms.setColorMap(colorScale.vectors);
    dellCallMesh.uniforms.setColorMap(colorScale.vectors);
    ciPosEndBarMesh.uniforms.setColorMap([vec]);
    ciPosEndVertStartsMesh.uniforms.setColorMap([vec]);
    ciPosEndVertEndsMesh.uniforms.setColorMap([vec]);

    track.requestRender();
  }

  function onInit() {
    track.setFilters([...state.filters]);
  }

  function updateFilterCounter(newCalls) {
    const { filterCounter: fc } = state;
    newCalls.forEach((x) => {
      const pathKey = `path-${x.pathoIndex}`;
      const zygoKey = `zygo-${x.zygosityIndex}`;
      const precKey = `prec-${x.precisionIndex}`;
      const typeKey = `type-${x.typeIndex}`;

      fc.set(zygoKey, (fc.get(zygoKey) || 0) + 1);
      fc.set(pathKey, (fc.get(pathKey) || 0) + 1);
      fc.set(precKey, (fc.get(precKey) || 0) + 1);
      fc.set(typeKey, (fc.get(typeKey) || 0) + 1);
    });
  }

  function calcFilters() {
    state.filters.forEach((x) => {
      x.totalCount = state.filterCounter.get(x.key) || 0;
    });
    track.setFilters([...state.filters]);
  }

  function onFiltersChange(updatedFilters) {
    state.filters = updatedFilters;
    track.setFilters([...state.filters]);
    setTimeout(() => applyFilters(), 50);
  }

  function getActiveFilterKeys() {
    return state.filters.filter((x) => x.value).map((x) => x.key);
  }

  function applyFilters() {
    const keys = getActiveFilterKeys();
    updateCall(state.dellyDups, dubCallMesh, keys);
    updateCall(state.dellyDels, dellCallMesh, keys);
    updateCallConf(state.ciPosEndBars, ciPosEndBarMesh, keys);
    updateCallConf(state.ciPosEndBars, ciPosEndVertStartsMesh, keys);
    updateCallConf(state.ciPosEndBars, ciPosEndVertEndsMesh, keys);

    track.requestRender();
  }

  function getState() {
    return state;
  }

  return {
    onInit,
    onReset,
    onPreferencesChanged,
    onRangeChange,
    onDataReceived,
    onFiltersChange,
    getState,
    meshes: [
      dubCallMesh,
      dellCallMesh,
      ciPosEndVertStartsMesh,
      ciPosEndVertEndsMesh,
      ciPosEndBarMesh,
    ],
  };
}

export default readTrackController;
